import {MatchmakingMatchTypes} from '@colonist/game-base-types'
import {ActiveMatchmakingMatchTypes} from '../../../shared/matchmaking/MatchmakingEnums'
import {uiManager} from './EntryHomepage'
import {SocketLobbySend} from '../../shared/scripts/socket/SocketLobbySend'
import {Scene} from './ui_html/UISceneTypes'
import {UIUrlParameterNames} from '../../shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../shared/scripts/UIEntryUrlParameterController'
import {UIUtilsEjsVariables} from '../../shared/scripts/UIUtilsEjsVariables'
import {clientI18n, clientIntl} from '../../shared/scripts/ClientI18n'
import {ReferralTypeEnum, ShareActivityLinkAnalytics} from '../../../shared/entity_datas/EntityDatas'
import {StorageWrapper} from '../../shared/scripts/StorageWrapper'
import {UIDiscordInstance} from './discord/child_iframe/UIDiscordInstance'
import {ClientUserState} from '../../shared/scripts/ClientUserState'
import {SDKRequest} from '../../../shared/DiscordActivityTypes'

export class EntryHomepageUrlParameterController extends UIEntryUrlParameterController {
    static loadParameterEvents() {
        const urlParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        for(const parameterName of urlParams.keys()) {
            const parameterValue = urlParams.get(parameterName)
            if(parameterValue == null) continue
            switch(parameterName as UIUrlParameterNames) {
                case UIUrlParameterNames.FindGameSelector: this.loadMatchmakingSelector(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.Campaign: this.loadCampaignMode(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.EnterMatchmaking: this.loadMatchmakingQueue(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.VideoAdTesting: return // intentionally returning to prevent ad test from being cleared from window
                case UIUrlParameterNames.Lobby: this.loadLobbyScene(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.MainPage: this.loadMainPageScene(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.DiscordLeaderboard: this.loadDiscordScene(parameterValue, Scene.DiscordLeaderboard); break
                case UIUrlParameterNames.DiscordStore: this.loadDiscordScene(parameterValue, Scene.DiscordStore); break
                case UIUrlParameterNames.DiscordAbout: this.loadDiscordScene(parameterValue, Scene.DiscordAbout); break
                case UIUrlParameterNames.DiscordCareers: this.loadDiscordScene(parameterValue, Scene.DiscordCareers); break
                case UIUrlParameterNames.DiscordCommunity: this.loadDiscordScene(parameterValue, Scene.DiscordCommunity); break
                case UIUrlParameterNames.DiscordFAQ: this.loadDiscordScene(parameterValue, Scene.DiscordFAQ); break
                case UIUrlParameterNames.DiscordPrivacyPolicy: this.loadDiscordScene(parameterValue, Scene.DiscordPrivacyPolicy); break
                case UIUrlParameterNames.DiscordPatchNotes: this.loadDiscordScene(parameterValue, Scene.DiscordPatchNotes); break
                case UIUrlParameterNames.DiscordProfile: this.loadDiscordScene(parameterValue, Scene.DiscordProfile); break
                case UIUrlParameterNames.DiscordGifting: this.loadDiscordGiftScene(parameterValue); break
                case UIUrlParameterNames.DiscordValues: this.loadDiscordScene(parameterValue, Scene.DiscordValues); break
                case UIUrlParameterNames.DiscordTerms: this.loadDiscordScene(parameterValue, Scene.DiscordTerms); break
                case UIUrlParameterNames.DiscordPressKit: this.loadDiscordScene(parameterValue, Scene.DiscordPressKit); break
                case UIUrlParameterNames.DiscordRefundPolicy: this.loadDiscordScene(parameterValue, Scene.DiscordRefundPolicy); break
                case UIUrlParameterNames.DiscordShareActivity: this.loadShareActivityPopup(); this.removeParameter(); break
            }
        }
    }

    static removeParameter() {
        window.history.replaceState({}, document.title, clientIntl.getLanguagePathPrefix() || '/')
    }

    static loadLobbyScene(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.Lobby)
        uiManager.header.activateLobbyButton()
    }

    static loadMainPageScene(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.MainPage)
    }

    static loadMatchmakingSelector(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.matchmakingController.showMatchmakingMatchTypeSelector()
    }

    static loadCampaignMode(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        void SocketLobbySend.continueCampaignMode()
    }

    static loadMatchmakingQueue(parameter: string) {
        const requestedMatchTypes: MatchmakingMatchTypes[] = []
        switch(Number(parameter) as ActiveMatchmakingMatchTypes) {
            case MatchmakingMatchTypes.UnrankedClassic4P: requestedMatchTypes.push(MatchmakingMatchTypes.UnrankedClassic4P); break
            case MatchmakingMatchTypes.RankedClassic4P: requestedMatchTypes.push(MatchmakingMatchTypes.RankedClassic4P); break
            case MatchmakingMatchTypes.UnrankedClassic1v1: requestedMatchTypes.push(MatchmakingMatchTypes.UnrankedClassic1v1); break
            case MatchmakingMatchTypes.RankedClassic1v1: requestedMatchTypes.push(MatchmakingMatchTypes.RankedClassic1v1); break
            case MatchmakingMatchTypes.RankedCitiesAndKnights4P: requestedMatchTypes.push(MatchmakingMatchTypes.RankedCitiesAndKnights4P); break
        }
        if(requestedMatchTypes.length == 0) return

        uiManager.matchmakingController.sendFindGameMessageToServer(requestedMatchTypes)
    }

    static loadDiscordScene(parameter: string, scene: Scene) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(scene)
        history.replaceState(null, '', window.location.pathname)
    }

    static loadShareActivityPopup() {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return

        UIDiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.ShareActivityLink, {
            message: clientI18n.t('strings:homePage.popups.discordShareActivity.text'),
            referrerId: ClientUserState.userState?.id.toString(),
            referralTypeId: ReferralTypeEnum.ShareActivityReturnToHomeAfterGameEnd.toString(),
            analyticsPopupTriggered: ShareActivityLinkAnalytics.ReturnToHomeAfterGameEndPopupTriggered,
            analyticsPopupSuccess: ShareActivityLinkAnalytics.ReturnToHomeAfterGameEndSuccessfullyShared,
        })

        StorageWrapper.setLastShareActivityLinkPopupTime(new Date())
    }

    private static loadDiscordGiftScene(parameter: string) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return
        if(parameter == this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.DiscordGifting, {recipientUsername: parameter})
    }
}
